import React, { memo, useEffect, useState } from 'react';
import DatePicker, { CalendarContainer } from 'react-datepicker';
import moment from 'moment';
import { Input, UncontrolledTooltip } from 'reactstrap';
import ReactDOM from 'react-dom';

import bn from '../../generalUtils/benames';
import {
  DATE_FORMAT_DOT_MDY,
  DATE_FORMAT_FULL_MONTH_YEAR,
  DATE_FORMAT_FULL_YEAR,
  DATE_FORMAT_YMD,
  DATE_TIME_FORMAT_DOT_MDY_WITH_AM_PM,
  TIME_FORMAT_HMS,
  TIME_FORMAT_WITH_AM_PM
} from '../../constants/dateFormatsConst';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { LG_MIN_WIDTH } from '../../constants/screenSizeConst';
import CustomButton from './CustomButton';

export const PICKERTYPE_TIME_CUSTOM = 'date_time_custom';
export const PICKERTYPE_DATE = 'date';
export const PICKERTYPE_DATE_APPLY = 'date_apply';
export const PICKERTYPE_DATE_APPLY_CUSTOM = 'date_apply_custom';
export const PICKERTYPE_TIME = 'time';
export const PICKERTYPE_TIME_HMS = 'time-hms';
export const PICKERTYPE_WEEK = 'week';
export const PICKERTYPE_MONTH = 'month';
export const PICKERTYPE_YEAR = 'year';
export const PICKERTYPE_DATERANGE = 'daterange';
export const PICKERTYPE_DATERANGE_TWOMONTH = 'daterange_twomonth';

export const TODAY_OPTIONS = 'Today';
export const YESTERDAY_OPTIONS = 'Yesterday';
export const LAST_7_DAYS_OPTIONS = 'Last 7 Days';
export const LAST_30_DAYS_OPTIONS = 'Last 30 Days';
export const CURRENT_WEEK = 'Current Week';
export const LAST_WEEK = 'Last Week';
export const THIS_MONTH_OPTIONS = 'This Month';
export const LAST_MONTH_OPTIONS = 'Last Month';
export const LAST_3_MONTHS_OPTIONS = 'Last 3 Months';
export const LAST_6_MONTHS_OPTIONS = 'Last 6 Months';
export const LAST_YEAR_OPTIONS = 'Last Year';

const bemClass = bn.create('multiRoleDatePicker');

/* PROPERTIES:
  ------------------
  Common Properties:
  - id
  - name
  - minDate
  - maxDate
  - buttonStyle
  - buttonIcon
  - buttonSize
  - isDateTextShown
  - onChange *
  - className
  - disabled
  - isLoading

  1. PICKERTYPE_DATE:
   - selected *
   - placeholderText

  2. PICKERTYPE_TIME:
   - selected *

  3. PICKERTYPE_WEEK:
   - selected *
   - selectedDays *

  4. PICKERTYPE_MONTH:
   - selected *
   - popperPlacement

  5. PICKERTYPE_YEAR:
  - selected *

  6. PICKERTYPE_DATERANGE:
   - startDate *
   - endDate *

  7. PICKERTYPE_DATERANGE_TWOMONTH:
   - startDate *
   - setStartDate *
   - endDate *
   - setEndDate *
   - toolTipText
   - onApply
   - selectedCustomOptions

  */

const MultiRoleDatePicker = props => {
  const {
    id,
    name,
    customChildren = null,
    usePortal = false,
    placeholderText = 'Select Date',
    pickerType = PICKERTYPE_DATE,
    isDisable = false,
    selected,
    onChange,
    showTimeInput = false,
    handleChangeTime,
    timeInputLabel = 'Time:',
    popperPlacement,
    startDate,
    endDate,
    selectedDays,
    setStartDate,
    setEndDate,
    buttonIcon = 'fa-calendar',
    buttonColor = 'dark-400',
    iconColor = 'text-secondary-100',
    dateButtonText = '',
    buttonIsOutline = true,
    onApply,
    minDate,
    maxDate = new Date(),
    isDateTextShown = true,
    buttonStyle = undefined,
    buttonSize = 'sm',
    toolTipText = undefined,
    selectedCustomOptions = [
      TODAY_OPTIONS,
      YESTERDAY_OPTIONS,
      LAST_7_DAYS_OPTIONS,
      LAST_30_DAYS_OPTIONS,
      THIS_MONTH_OPTIONS,
      LAST_MONTH_OPTIONS
    ],
    disabled = false,
    isLoading = false,
    className = '',
    isClearable = false,
    showNow = false,
    timeZoneLabel = ''
  } = props;

  const today = moment().startOf('day');
  const endTodayDay = today.clone().endOf('day');

  const yesterday = today.clone().subtract(1, 'day').startOf('day');
  const endYestrerDay = yesterday.clone().endOf('day');

  const last7Days = today.clone().subtract(6, 'days').startOf('day');
  const last30Days = today.clone().subtract(29, 'days').startOf('day');
  const startOfCurrentMonth = today.clone().startOf('month');

  const currentWeekStart = today.clone().subtract(1, 'days').startOf('week');
  const currentWeekEnd = today.clone().endOf('day');

  const lastWeekStart = today.clone().subtract(1, 'week').startOf('week');
  const lastWeekEnd = today.clone().subtract(1, 'week').endOf('week');

  const startOfLastMonth = today.clone().subtract(1, 'month').startOf('month');
  const endOfLastMonth = today.clone().subtract(1, 'month').endOf('month');

  const last3MonthsStart = today.clone().subtract(3, 'months').startOf('month');
  const last3MonthsEnd = today.clone().subtract(1, 'months').endOf('month');

  const last6MonthsStart = today.clone().subtract(6, 'months').startOf('month');
  const last6MonthsEnd = today.clone().subtract(1, 'months').endOf('month');

  const lastYearStart = today.clone().subtract(1, 'year').startOf('year');
  const lastYearEnd = today.clone().subtract(1, 'year').endOf('year');

  const initialRangeArr = [
    {
      label: TODAY_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: today,
      edate: endTodayDay
    },
    {
      label: YESTERDAY_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: yesterday,
      edate: endYestrerDay
    },
    {
      label: LAST_7_DAYS_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: last7Days,
      edate: endTodayDay
    },
    {
      label: CURRENT_WEEK,
      activeStatus: false,
      disabled: false,
      sdate: currentWeekStart,
      edate: currentWeekEnd
    },
    {
      label: LAST_WEEK,
      activeStatus: false,
      disabled: false,
      sdate: lastWeekStart,
      edate: lastWeekEnd
    },
    {
      label: LAST_30_DAYS_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: last30Days,
      edate: endTodayDay
    },
    {
      label: THIS_MONTH_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: startOfCurrentMonth,
      edate: endTodayDay
    },
    {
      label: LAST_MONTH_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: startOfLastMonth,
      edate: endOfLastMonth
    },
    {
      label: LAST_3_MONTHS_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: last3MonthsStart,
      edate: last3MonthsEnd
    },
    {
      label: LAST_6_MONTHS_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: last6MonthsStart,
      edate: last6MonthsEnd
    },
    {
      label: LAST_YEAR_OPTIONS,
      activeStatus: false,
      disabled: false,
      sdate: lastYearStart,
      edate: lastYearEnd
    }
  ];

  const [showRangeArr, setShowRangeArr] = useState(initialRangeArr);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const { width } = useWindowDimensions();
  const isMobileScreen = width < LG_MIN_WIDTH;

  useEffect(() => {
    if (startDate && endDate) {
      const startdate = moment(startDate).format(DATE_FORMAT_YMD);
      const enddate = moment(endDate).format(DATE_FORMAT_YMD);
      let activeIndex = -1;

      const activeItemRangeArr = showRangeArr.map((obj, index) => {
        const isActive =
          obj.sdate &&
          obj.sdate.format(DATE_FORMAT_YMD) === startdate &&
          obj.edate &&
          obj.edate.format(DATE_FORMAT_YMD) === enddate;
        if (isActive) {
          activeIndex = index; // Track the index of the active item
        }
        return { ...obj, activeStatus: false };
      });

      if (activeIndex >= 0) {
        // Set the last active item to true, if any
        activeItemRangeArr[activeIndex].activeStatus = true;
      }

      setShowRangeArr(activeItemRangeArr);
    }
  }, []);

  useEffect(() => {
    if (minDate) {
      const momentMinDate = moment(minDate).startOf('day');
      const momentMaxDate = moment(maxDate).endOf('day');
      const todayStart = moment().startOf('day');
      const todayEnd = moment().endOf('day');

      const newArr = showRangeArr?.map(obj => {
        const sdate = moment(obj.sdate).startOf('day');
        const edate = moment(obj.edate).endOf('day');
        let isWithinRange = false;

        if (sdate.isValid() && edate.isValid()) {
          const isToday = sdate.isSame(todayStart, 'day') && edate.isSame(todayEnd, 'day');

          isWithinRange = isToday
            ? momentMinDate.isSameOrBefore(sdate, 'day')
            : momentMinDate.isSameOrBefore(edate, 'day') && momentMaxDate.isSameOrAfter(sdate, 'day');
        }

        return {
          ...obj,
          disabled: !isWithinRange
        };
      });

      setShowRangeArr(newArr);
    }
  }, [minDate, maxDate]);

  const getStartNEndDateSelection = (startdate, enddate) => {
    const start = moment(startdate).format(DATE_FORMAT_DOT_MDY);
    if (enddate) {
      const end = moment(enddate).format(DATE_FORMAT_DOT_MDY);
      return `${start} - ${end}`;
    }
    return `${start} - `;
  };

  const handlePresetClick = async xitem => {
    const { sdate, edate } = xitem;
    const start = sdate?.toDate() || null;
    const end = edate?.toDate() || null;

    setStartDate(start, end);
    setEndDate(end, start);

    const newArr = showRangeArr.map(obj => ({ ...obj, activeStatus: obj.label === xitem?.label }));
    setShowRangeArr(newArr);

    if (!onApply) {
      setOpenDatePicker(false);
    }
  };

  const MyContainer = ({ className, children, customChildren = false }) => {
    const filteredInitialRangeArr = showRangeArr.filter(option =>
      selectedCustomOptions.includes(option.label)
    );

    let width;

    if (pickerType === PICKERTYPE_DATERANGE_TWOMONTH && customChildren) {
      width = isMobileScreen ? '17rem' : '930px';
    } else if (pickerType !== PICKERTYPE_DATE_APPLY) {
      width = isMobileScreen ? '17rem' : '590px';
    }

    const styleObj = width
      ? {
          width,
          ...(isMobileScreen && {
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap'
          })
        }
      : {};

    return (
      <div
        className={`customdaterangepicker ${
          pickerType === PICKERTYPE_DATE_APPLY ? 'custom-month-apply' : ''
        }`}
        style={styleObj}
      >
        {pickerType === PICKERTYPE_DATERANGE_TWOMONTH && (
          <div style={customChildren ? styleObj : {}}>
            <div className="ranges">
              <ul>
                {filteredInitialRangeArr.map(x => {
                  let classAdd = '';
                  if (x.activeStatus) {
                    classAdd += 'active';
                  }
                  if (x.disabled) {
                    classAdd += ' disabled';
                  }

                  return (
                    <li
                      key={x.label}
                      data-range-key={x.label}
                      className={classAdd}
                      onClick={() => {
                        if (!x.disabled) {
                          handlePresetClick(x);
                        }
                      }}
                    >
                      {x.label}
                    </li>
                  );
                })}
              </ul>
            </div>
            {customChildren ? (
              <div className={`ranges_email float-right ${isMobileScreen ? 'w-100' : 'w-35 m-3 '}`}>
                {customChildren}
              </div>
            ) : (
              <></>
            )}{' '}
          </div>
        )}
        <CalendarContainer className={className}>
          <div style={{ position: 'relative' }}>{children}</div>
        </CalendarContainer>
        {pickerType === PICKERTYPE_DATE_APPLY_CUSTOM && customChildren && (
          <div className={`ranges_email float-right ${isMobileScreen ? 'w-100' : 'w-50 m-3 '}`}>
            {customChildren}
          </div>
        )}
        <div className="drp-buttons">
          <span
            className={
              pickerType === PICKERTYPE_DATERANGE_TWOMONTH && !endDate
                ? 'drp-selected text-danger'
                : 'drp-selected'
            }
          >
            {pickerType === PICKERTYPE_DATERANGE_TWOMONTH
              ? startDate
                ? getStartNEndDateSelection(startDate, endDate)
                : 'Select Date Range'
              : ''}
            {pickerType === PICKERTYPE_DATE_APPLY
              ? selected
                ? moment(selected).format(DATE_FORMAT_DOT_MDY)
                : 'Select Date'
              : ''}
            {pickerType === PICKERTYPE_DATE_APPLY_CUSTOM
              ? selected
                ? moment(selected).format(DATE_FORMAT_DOT_MDY)
                : 'Select Date'
              : ''}
            {pickerType === PICKERTYPE_DATERANGE_TWOMONTH && !endDate ? (
              <span className="drp-disabled">Please select end date!</span>
            ) : null}
          </span>
          {pickerType !== PICKERTYPE_DATE_APPLY && (
            <CustomButton
              color="default"
              className="cancelBtn"
              onClick={() => {
                setOpenDatePicker(false);
              }}
            >
              Close
            </CustomButton>
          )}
          {onApply && (
            <CustomButton
              id=""
              color="primary"
              disabled={(pickerType === PICKERTYPE_DATERANGE_TWOMONTH ? !endDate : false) || isDisable}
              className="applyBtn"
              onClick={() => {
                if (onApply) {
                  onApply();
                }
                setOpenDatePicker(false);
              }}
            >
              Apply
            </CustomButton>
          )}
        </div>
      </div>
    );
  };

  const CustomTimeInput = ({ date, onChangeCustom }) => {
    const value = date instanceof Date && !isNaN(date) ? date.toLocaleTimeString('it-IT') : '';

    return (
      <Input
        type="time"
        step="1"
        value={value}
        onChange={event => {
          const newTime = event.target.value;
          if (!newTime) {
            return false;
          }
          const [hours, minutes, secondsMilliseconds] = newTime.split(':');
          const [seconds] = secondsMilliseconds.split('.');

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);
          onChangeCustom(date, event.target.value);
        }}
      />
    );
  };

  return (
    <div className={bemClass.b(className)}>
      {pickerType === PICKERTYPE_DATE && (
        <DatePicker
          id={id}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          name={name}
          selected={selected}
          onChange={onChange}
          showMonthDropdown
          disabled={isLoading || disabled}
          showYearDropdown
          maxDate={maxDate}
          minDate={minDate}
          popperPlacement="top-end"
          showTimeInput={showTimeInput}
          customInput={
            <CustomButton
              id={id ? `btn${pickerType}${id}` : undefined}
              key={id}
              outline={buttonIsOutline}
              onClick={onChange}
              size={buttonSize}
              color={buttonColor}
              className="custom-date-input"
              style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '13rem') : '' }}
            >
              {isLoading ? (
                <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
              ) : (
                <i className={`fa fa-fw ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
              )}
              {!isMobileScreen && isDateTextShown && (
                <span className="text-secondary-100">
                  {selected
                    ? showTimeInput
                      ? moment(selected).format(DATE_TIME_FORMAT_DOT_MDY_WITH_AM_PM)
                      : moment(selected).format(DATE_FORMAT_DOT_MDY)
                    : placeholderText}
                </span>
              )}
              {isClearable && isDateTextShown && selected && (
                <span
                  className="pull-right ml-1"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <i className="fa fa-times-circle" />
                </span>
              )}
            </CustomButton>
          }
        />
      )}

      {pickerType === PICKERTYPE_DATE_APPLY && (
        <>
          <CustomButton
            id={id ? `btn${pickerType}${id}` : undefined}
            outline={buttonIsOutline}
            onClick={e => {
              e.preventDefault();
              let check = !openDatePicker;
              const status = e.target.getAttribute('data-close');
              if (status) {
                check = JSON.parse(status.toLowerCase());
              }
              setOpenDatePicker(check);
              e.target.removeAttribute('data-close');
            }}
            disabled={isLoading || disabled}
            color={buttonColor}
            size={buttonSize}
            className="custom-date-input"
            style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '12.7rem') : '' }}
          >
            {isLoading ? (
              <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
            ) : (
              <i className={`fa ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
            )}
            {!isMobileScreen && isDateTextShown && (
              <span className="ml-1 text-secondary-100">
                {dateButtonText
                  ? dateButtonText
                  : startDate
                  ? getStartNEndDateSelection(startDate, endDate)
                  : 'Select Date Range'}
              </span>
            )}
          </CustomButton>

          {openDatePicker && (
            <DatePicker
              id={id}
              {...(usePortal
                ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
                : {})}
              name={name}
              open={openDatePicker}
              selected={selected}
              onChange={onChange}
              showMonthDropdown
              showYearDropdown
              maxDate={maxDate}
              minDate={minDate}
              calendarContainer={MyContainer}
              onClickOutside={e => {
                e.preventDefault();
                e.target.setAttribute('data-close', false);
                setOpenDatePicker(false);
              }}
              closeOnScroll={() => {
                setOpenDatePicker(false);
              }}
              customInput={<div className="d-none" />}
              popperPlacement="top-end"
              disabled={isLoading || disabled}
            />
          )}
        </>
      )}

      {pickerType === PICKERTYPE_DATE_APPLY_CUSTOM && (
        <>
          <CustomButton
            id={id ? `btn${pickerType}${id}` : undefined}
            outline={buttonIsOutline}
            onClick={e => {
              e.preventDefault();
              let check = !openDatePicker;
              const status = e.target.getAttribute('data-close');
              if (status) {
                check = JSON.parse(status.toLowerCase());
              }
              setOpenDatePicker(check);
              e.target.removeAttribute('data-close');
            }}
            disabled={isLoading || disabled}
            color={buttonColor}
            size={buttonSize}
            className="custom-date-input"
            style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '12.7rem') : '' }}
          >
            {isLoading ? (
              <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
            ) : (
              <i className={`fa ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
            )}
            {!isMobileScreen && isDateTextShown && (
              <span className="ml-1 text-secondary-100">
                {dateButtonText
                  ? dateButtonText
                  : startDate
                  ? getStartNEndDateSelection(startDate, endDate)
                  : 'Select Date Range'}
              </span>
            )}
          </CustomButton>

          {openDatePicker && (
            <DatePicker
              id={id}
              {...(usePortal
                ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
                : {})}
              name={name}
              open={openDatePicker}
              selected={selected}
              onChange={onChange}
              maxDate={maxDate}
              minDate={minDate}
              calendarContainer={propscal => (
                <MyContainer {...propscal} customChildren={isMobileScreen ? null : customChildren} />
              )}
              onClickOutside={e => {
                e.preventDefault();
                e.target.setAttribute('data-close', false);
                setOpenDatePicker(false);
              }}
              closeOnScroll={() => {
                setOpenDatePicker(false);
              }}
              customInput={<div className="d-none" />}
              popperPlacement="top-end"
              disabled={isLoading || disabled}
            />
          )}
        </>
      )}

      {pickerType === PICKERTYPE_TIME && (
        <DatePicker
          id={id}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          name={name}
          selected={selected}
          onChange={onChange}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={60}
          timeCaption="Time"
          customInput={
            <CustomButton
              id={id ? `btn${pickerType}${id}` : undefined}
              key={id}
              size={buttonSize}
              className="custom-date-input"
              outline={buttonIsOutline}
              onClick={onChange}
              color={buttonColor}
              disabled={isLoading || disabled}
              style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '13rem') : '' }}
            >
              {isLoading ? (
                <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
              ) : (
                <i className="fa fa-fw fa-clock-o mr-2" />
              )}
              {!isMobileScreen && isDateTextShown && (
                <span className="text-secondary-100">
                  {selected ? moment(selected).format(TIME_FORMAT_WITH_AM_PM) : 'Time'}
                </span>
              )}
              {isClearable && isDateTextShown && selected && (
                <span
                  className="pull-right ml-1"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <i className="fa fa-times-circle" />
                </span>
              )}
            </CustomButton>
          }
        />
      )}

      {pickerType === PICKERTYPE_TIME_HMS && (
        <DatePicker
          id={id}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          name={name}
          selected={selected}
          onChange={onChange}
          dateFormat={TIME_FORMAT_HMS}
          showTimeInput
          showTimeSelectOnly
          timeInputLabel={timeInputLabel}
          timeCaption="Time"
          inline
          customTimeInput={<CustomTimeInput onChangeCustom={handleChangeTime} />}
        />
      )}

      {pickerType === PICKERTYPE_WEEK && (
        <div className="weekpicker">
          <DatePicker
            id={id}
            {...(usePortal
              ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
              : {})}
            name={name}
            selected={selected}
            onChange={onChange}
            highlightDates={[{ 'react-datepicker__day--highlighted-custom-1': selectedDays }]}
            showMonthDropdown
            showYearDropdown
            customInput={
              <CustomButton
                id={id ? `btn${pickerType}${id}` : undefined}
                type="button"
                size={buttonSize}
                outline={buttonIsOutline}
                className="custom-date-input"
                onClick={onChange}
                color={buttonColor}
                disabled={isLoading || disabled}
                style={
                  buttonStyle || {
                    width: isDateTextShown ? (isMobileScreen ? 'auto' : '13rem') : '',
                    justifyContent: 'center',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }
              >
                {isLoading ? (
                  <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
                ) : (
                  <i className={`fa fa-fw ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
                )}
                {!isMobileScreen && isDateTextShown && (
                  <span className="ml-1 text-secondary-100">
                    {selectedDays
                      ? getStartNEndDateSelection(selectedDays[0], selectedDays[6])
                      : 'Select Week'}
                  </span>
                )}
                {isClearable && isDateTextShown && (dateButtonText || selected) && (
                  <span
                    className="pull-right ml-1"
                    onClick={e => {
                      e.stopPropagation();
                      onChange(null);
                    }}
                  >
                    <i className="fa fa-times-circle" />
                  </span>
                )}
              </CustomButton>
            }
          />
        </div>
      )}

      {pickerType === PICKERTYPE_MONTH && (
        <DatePicker
          id={id}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          name={name}
          selected={selected}
          onChange={onChange}
          showMonthYearPicker
          maxDate={maxDate}
          minDate={minDate}
          popperPlacement={popperPlacement || 'bottom-end'}
          customInput={
            <CustomButton
              id={id ? `btn${pickerType}${id}` : undefined}
              key={moment(selected).format(DATE_FORMAT_FULL_MONTH_YEAR)}
              size={buttonSize}
              className="custom-date-input"
              outline={buttonIsOutline}
              color={buttonColor}
              onClick={onChange}
              disabled={isLoading || disabled}
              style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '13rem') : '' }}
            >
              {isLoading ? (
                <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
              ) : (
                <i className={`fa fa-fw ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
              )}
              {!isMobileScreen && isDateTextShown && (
                <span className="ml-2 text-secondary-100">
                  {dateButtonText
                    ? dateButtonText
                    : selected
                    ? moment(selected).format(DATE_FORMAT_FULL_MONTH_YEAR)
                    : 'Select Month'}
                </span>
              )}
              {isClearable && isDateTextShown && (dateButtonText || selected) && (
                <span
                  className="pull-right ml-1"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <i className="fa fa-times-circle" />
                </span>
              )}
            </CustomButton>
          }
        />
      )}

      {pickerType === PICKERTYPE_YEAR && (
        <DatePicker
          id={id}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          name={name}
          selected={selected}
          onChange={onChange}
          showYearPicker
          minDate={minDate}
          maxDate={maxDate}
          yearItemNumber={10}
          customInput={
            <CustomButton
              id={id ? `btn${pickerType}${id}` : undefined}
              key={moment(selected).format(DATE_FORMAT_FULL_YEAR)}
              size={buttonSize}
              className="custom-date-input"
              outline={buttonIsOutline}
              color={buttonColor}
              onClick={onChange}
              disabled={isLoading || disabled}
              style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? '2rem' : '13rem') : '' }}
            >
              {isLoading ? (
                <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
              ) : (
                <i className={`fa fa-fw ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
              )}
              {!isMobileScreen && isDateTextShown && (
                <span className="ml-2 text-secondary-100">
                  {dateButtonText
                    ? dateButtonText
                    : selected
                    ? moment(selected).format(DATE_FORMAT_FULL_YEAR)
                    : 'Select Year'}
                </span>
              )}
            </CustomButton>
          }
        />
      )}

      {pickerType === PICKERTYPE_DATERANGE && (
        <DatePicker
          popperContainer={({ children }) => children}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          id={id}
          name={name}
          disabled={disabled}
          selected={startDate}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          onChange={onChange}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          minDate={minDate}
          maxDate={maxDate}
          customInput={
            <CustomButton
              id={id ? `btn${pickerType}${id}` : undefined}
              key={id}
              size={buttonSize}
              className="custom-date-input"
              outline={buttonIsOutline}
              color={buttonColor}
              style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '13rem') : '' }}
              onClick={onChange}
              disabled={isLoading || disabled}
            >
              {isLoading ? (
                <i className="fa fa-spinner fa-spin mx-1 fa-thin fs-15" />
              ) : (
                <i className={`fa fa-fw ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
              )}
              {!isMobileScreen && isDateTextShown && (
                <span className="ml-2 text-secondary-100">
                  {startDate
                    ? getStartNEndDateSelection(startDate, endDate)
                    : dateButtonText
                    ? dateButtonText
                    : 'Select Date Range'}
                </span>
              )}
              {isClearable && isDateTextShown && startDate && (
                <span
                  className="pull-right ml-1"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <i className="fa fa-times-circle" />
                </span>
              )}
            </CustomButton>
          }
        />
      )}

      {pickerType === PICKERTYPE_DATERANGE_TWOMONTH && (
        <>
          <CustomButton
            id={id ? `btn${pickerType}${id}` : undefined}
            outline={buttonIsOutline}
            onClick={e => {
              e.preventDefault();
              let check = !openDatePicker;
              const status = e.target.getAttribute('data-close');
              if (status) {
                check = JSON.parse(status.toLowerCase());
              }
              setOpenDatePicker(check);
              e.target.removeAttribute('data-close');
            }}
            color={buttonColor}
            size={buttonSize}
            disabled={isLoading || disabled}
            className="custom-date-input twomonthcustom"
            style={buttonStyle || { width: isDateTextShown ? (isMobileScreen ? 'auto' : '14rem') : '' }}
          >
            {timeZoneLabel && (
              <span
                className="tzone"
                onClick={e => {
                  e.stopPropagation();
                }}
                title="PST time"
              >
                {timeZoneLabel}
              </span>
            )}
            {isLoading ? (
              <i className="fa fa-spinner fa-spin fa-thin fs-15 mr-1" />
            ) : (
              <i className={`fa ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
            )}
            {!isMobileScreen && isDateTextShown && (
              <span className="ml-1 text-secondary-100">
                {dateButtonText
                  ? dateButtonText
                  : startDate
                  ? getStartNEndDateSelection(startDate, endDate)
                  : 'Select Date Range'}
              </span>
            )}
            {isClearable && isDateTextShown && startDate && (
              <span
                className="pull-right ml-1"
                onClick={e => {
                  e.stopPropagation();
                  onChange(null);
                }}
              >
                <i className="fa fa-times-circle" />
              </span>
            )}
          </CustomButton>
          {toolTipText && (
            <UncontrolledTooltip placement="bottom" target={id ? `btn${pickerType}${id}` : undefined}>
              {toolTipText}
            </UncontrolledTooltip>
          )}
          {openDatePicker && (
            <DatePicker
              id={id}
              {...(usePortal
                ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
                : {})}
              name={name}
              open={openDatePicker}
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              monthsShown={isMobileScreen ? 1 : 2}
              selectsRange
              onChange={onChange}
              maxDate={maxDate}
              minDate={minDate}
              showMonthDropdown
              showYearDropdown
              onClickOutside={e => {
                e.preventDefault();
                e.target.setAttribute('data-close', false);
                setOpenDatePicker(false);
              }}
              closeOnScroll={() => {
                setOpenDatePicker(false);
              }}
              customInput={<div className="d-none" />}
              popperPlacement="top-end"
              calendarContainer={propscal => (
                <MyContainer {...propscal} customChildren={isMobileScreen ? null : customChildren} />
              )}

              // popperModifiers={{
              //   preventOverflow: {
              //     enabled: true,
              //     escapeWithReference: true,
              //     boundariesElement: 'viewport'
              //   },
              //   flip: {
              //     enabled: true,
              //     behavior: ['top', 'bottom', 'left', 'right']
              //   }
              // }}
            />
          )}
        </>
      )}
      {pickerType === PICKERTYPE_TIME_CUSTOM && (
        <DatePicker
          id={id}
          {...(usePortal
            ? { popperContainer: ({ children }) => ReactDOM.createPortal(children, document.body) }
            : {})}
          name={name}
          selected={selected}
          onChange={onChange}
          disabled={isLoading || disabled}
          showMonthDropdown
          showYearDropdown
          maxDate={maxDate}
          minDate={minDate}
          popperPlacement="top-end"
          showTimeInput={showTimeInput}
          customInput={
            <div className={disabled ? 'now-picker disabled' : 'now-picker'}>
              {timeZoneLabel && (
                <span
                  className="tzone"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  {timeZoneLabel}
                </span>
              )}
              <div className="now-wrapper">
                <span className="icon">
                  {isLoading ? (
                    <i className="fa fa-spinner fa-spin fa-thin" />
                  ) : (
                    <i className={`fa fa-fw ${buttonIcon} ${iconColor ? iconColor : 'text-secondary-100'}`} />
                  )}
                </span>
                {!isMobileScreen && isDateTextShown && (
                  <span className="label" title="Set date(time)">
                    {dateButtonText
                      ? dateButtonText
                      : selected
                      ? showTimeInput
                        ? moment(selected).format(DATE_TIME_FORMAT_DOT_MDY_WITH_AM_PM)
                        : moment(selected).format(DATE_FORMAT_DOT_MDY)
                      : placeholderText}
                  </span>
                )}
              </div>
              {showNow && (
                <span
                  title="Reset current date(time)"
                  className="icon icon-btn"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(new Date());
                  }}
                >
                  <i className="fa fa-fw fa-clock-o" />
                </span>
              )}
              {isClearable && (
                <span
                  title="Clear"
                  className="icon icon-btn"
                  onClick={e => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <i className="fa fa-fw fa-times-circle" />
                </span>
              )}
            </div>
          }
        />
      )}
    </div>
  );
};

MultiRoleDatePicker.displayName = 'MultiRoleDatePicker';
export default memo(MultiRoleDatePicker);
